import { Box, Button, Grid2 as Grid } from "@mui/material";
import React, { Dispatch, SetStateAction, useRef } from "react";
import { isEmpty } from "lodash";

import {
  OrderingPhysicianDetails,
  PrimaryPhysicianDetails,
} from "../../../models/PhysicianDetails";
import { FormInputBox, SwitchGrid, Body } from "../styles/OrderingPhysician";
import {
  physicianFormProps,
  physicianNames,
  GetNPPESDataActionDispatchTypes,
} from "../../../constants/PhysicianDetails";
import { rules } from "../../../utils/validation/Validation";
import { FormInputText } from "../../../components/FormPriorAuthReAuth/FormInputText";
import { PhoneFaxExtInput } from "../../../components/FormPriorAuthReAuth/PhoneFaxExtInput";
import { AddressFormInputUseForm } from "../../../components/FormPriorAuthReAuth/AddressFormInputUseForm";
import NppesSearchComponent from "../../../components/nppesSearchComponent/container/NppesSearchContainer";
import {
  ButtonMinWidth,
  ClearButtonGrid,
} from "../styles/style";
import { UseFormGetValues, UseFormReset, UseFormSetValue } from "react-hook-form";
export interface PropsFromState {
  choosePhysician?: (
    option: PrimaryPhysicianDetails | OrderingPhysicianDetails
  ) => void;

  names: any;
  physicianFormName: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setOpenAutocomplete: Dispatch<SetStateAction<boolean>>;
  openAutocomplete: boolean;
  isReadOnly?: boolean;
  noValidate?: boolean;
  renderClearButton?: () => void;
  setAddManually?: (value: boolean) => void;
  physician?: PrimaryPhysicianDetails | OrderingPhysicianDetails | null;
  control: any;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  reset?: UseFormReset<any>;
  type: string;
  isSourceName?: boolean;
  setIsReadOnly?: (value: boolean) => void;
}
export interface PropsFromDispatch {
  resetStates: (actionType: string) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const PhysicianInputForm: React.FC<AllProps> = ({
  choosePhysician,
  control,
  getValues,
  names,
  open,
  reset,
  setOpen,
  setValue,
  physicianFormName,
  setAddManually,
  resetStates,
  openAutocomplete,
  setOpenAutocomplete,
  isReadOnly,
  noValidate,
  renderClearButton,
  isSourceName,
  type,
  setIsReadOnly,
}: AllProps) => {
  const handleSelect = (option: any) => {
    choosePhysician?.(option);
    if (!isEmpty(physicianFormName)) {
      setValue(physicianFormName, option.physicianName || null);
    }
    setAddManually && setAddManually(false);
    resetStates &&
      resetStates(GetNPPESDataActionDispatchTypes.GET_NPPES_DATA_RESET);
    setOpen && setOpen(false);
    setIsReadOnly && setIsReadOnly(false);
  };

  return (
    <>
      <Grid container>
        <Grid
          size={{ xs: 12 }}
          data-testid="patient-header-primary-physician"
          sx={SwitchGrid(isReadOnly ? isReadOnly : false)}
        >
          <NppesSearchComponent
            NppesOpen={open}
            NppesSetOpen={setOpen}
            handleSelect={handleSelect}
            openAutocomplete={openAutocomplete}
            setOpenAutocomplete={setOpenAutocomplete}
            nppesSearchInjectedto={physicianFormName}
            type={type}
          />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={[Body, SwitchGrid(isReadOnly ? isReadOnly : false)]}
        >
          <PhysicianFormInputs
            control={control}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            names={names}
            noValidate={noValidate}
            physicianFormName={physicianFormName}
            renderClearButton={renderClearButton ?? (() => {})}
            isSourceName={isSourceName}
            isReadOnly={isReadOnly}
          />
        </Grid>
      </Grid>
    </>
  );
};

type PhysicianFormInputsProps = {
  control: any;
  reset: any;
  getValues: any;
  setValue: any;
  names: any;
  renderClearButton?: () => void;
  physicianFormName: string;
  isHeader?: boolean;
  noValidate?: boolean;
  isSourceName?: boolean;
  isReadOnly?: boolean;
};

export const PhysicianFormInputs = (props: PhysicianFormInputsProps) => {
  const {
    control,
    reset,
    getValues,
    setValue,
    names,
    isHeader,
    noValidate,
    physicianFormName,
    renderClearButton,
    isSourceName,
    isReadOnly,
  } = props;

  const searchInput = useRef(null);
  const labels = [
    {
      label: "Street 1: ",
      name: names.address[0],
      helper: noValidate ? false : rules.streetName1,
      textLength: 100,
      xs: 2.5,
    },
    {
      label: "Street 2: ",
      name: names.address[1],
      helper:
        physicianFormName === physicianNames.primary
          ? false
          : rules.streetName2,
      textLength: 100,
      xs: 2.5,
    },
    {
      label: "City: ",
      name: names.address[2],
      helper: noValidate ? rules.validCity : rules.city,
      textLength: 100,
      xs: 2.5,
    },
    {
      label: "State: ",
      name: names.address[4],
      helper: noValidate ? rules.validState : rules.state,
      textLength: 100,
    },
    {
      label: "County: ",
      name: names.address[3],
      helper: rules.validCounty,
      textLength: 100,
    },
    {
      label: "Zip Code: ",
      name: names.address[5],
      helper: noValidate
        ? {
            pattern: rules.zipcode.pattern,
          }
        : rules.physicianZipcode,
      textLength: 10,
    },
    {
      name: names.address[6], // hidden latitude
    },
    {
      name: names.address[7], // hidden longitude
    },
  ];

  const formProps = physicianFormProps(physicianFormName);
  let faxRules;
  let phoneRules;

  if (noValidate) {
    phoneRules = rules.phonePattern;
  } else if (physicianFormName === physicianNames.primary) {
    phoneRules = rules.phonePattern;
  } else if (isReadOnly) {
    phoneRules = false;
  } else {
    phoneRules = rules.phone;
  }
  if (
    physicianFormName === physicianNames.primary ||
    physicianFormName === physicianNames.following
  ) {
    faxRules = {
      ...rules.fax,
      required: false,
    };
  } else if (isSourceName) {
    faxRules = {
      ...rules.fax,
      required: false,
    };
  } else if (noValidate || isReadOnly) {
    faxRules = false;
  } else {
    faxRules = rules.fax;
  }
  return (
    <>
      <Grid size={{ xs: 12 }} pt={isHeader ? "0.5rem" : "1.5rem"}>
        <Grid container>
          <Grid size={{ xs: 3 }}>
            <Box sx={FormInputBox}>
              <FormInputText
                name={names.physicianName}
                control={control}
                label={formProps.physicianName}
                fieldrequired={"true"}
                helper={noValidate ? rules.validPhysicianName : formProps.rule}
                placeholder="Add here..."
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 2 }}>
            <Box sx={FormInputBox}>
              <FormInputText
                name={names.physicianNpiId}
                control={control}
                label={formProps.physicianNpi}
                textLength={10}
                fieldrequired={"true"}
                helper={noValidate ? rules.validNpi : rules.npiRequired}
                placeholder="Add here..."
                inputType="onlyNumber"
              />
            </Box>
          </Grid>
          {isSourceName && (
            <Grid size={{ xs: 2.5 }}>
              <Box sx={FormInputBox}>
                <FormInputText
                  name={"referralSource.referralSourceName"}
                  control={control}
                  label={"Requestor Contact Name:"}
                  textLength={48}
                  fieldrequired={"true"}
                  placeholder="Add here..."
                  helper={noValidate ? false : rules.contactName}
                />
              </Box>
            </Grid>
          )}
          {!isSourceName && (
            <Grid size={{ xs: 1.5 }}>
              <Box sx={FormInputBox}>
                <FormInputText
                  name={names.taxId}
                  control={control}
                  label={"Tax ID:"}
                  textLength={15}
                  fieldrequired={"true"}
                  placeholder="Add here..."
                  inputType="onlyNumber"
                />
              </Box>
            </Grid>
          )}
          <Grid size={{ xs: 3 }}>
            <Box sx={FormInputBox}>
              <PhoneFaxExtInput
                label="Phone:"
                control={control}
                phoneFaxName={names.phoneNo}
                extName={names.phoneExt}
                rules={phoneRules}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 2.5 }}>
            <Box sx={FormInputBox}>
              <PhoneFaxExtInput
                label="Fax:"
                control={control}
                phoneFaxName={names.fax}
                rules={faxRules}
                isFax
              />
            </Box>
          </Grid>
          {isSourceName && (
            <Grid size={{ xs: 2 }}>
              <Box sx={FormInputBox}>
                <FormInputText
                  name={"referralSource.referralSourceEmail"}
                  control={control}
                  label={"Email :"}
                  textLength={48}
                  fieldrequired={"true"}
                  placeholder="Add here..."
                  helper={rules.emailPattern}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container>
        <Grid size={{ xs: 11 }}>
          <AddressFormInputUseForm
            control={control}
            searchInput={searchInput}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            labels={labels}
            addressVariant="subtitle1"
            addressLabel="ADDRESS DETAILS:"
          />
        </Grid>
        <Grid size={{ xs: 1 }} pr={5} sx={ClearButtonGrid}>
          <Button
            variant="contained"
            sx={ButtonMinWidth}
            onClick={() => renderClearButton && renderClearButton()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default PhysicianInputForm;
